import {
  ChangeDetectionStrategy,
  Component,
  effect,
  inject,
  Inject,
  OnInit,
  signal,
} from '@angular/core';
import {
  FormControl,
  FormGroup,
  FormsModule,
  ReactiveFormsModule,
  Validators,
} from '@angular/forms';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { MAT_DIALOG_DATA, MatDialogActions, MatDialogContent } from '@angular/material/dialog';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import dayjs from 'dayjs';
import { RequestStateEnum } from '../../../../enums/app.enum';
import { ActivityService } from '../../../../services/activity.service';
import { ModalService } from '../../../../services/modal.service';
import { ToastService } from '../../../../services/toast.service';
import { ActivityStore } from '../../../../stores/activity.store';
import { AlertComponent } from '../../../atoms/alert/alert.component';
import { AlertTypeEnum } from '../../../atoms/alert/alert.types';
import { ButtonComponent } from '../../../atoms/button/button.component';
import { DropdownComponent } from '../../../atoms/dropdown/dropdown.component';
import { InputComponent } from '../../../atoms/input/input.component';
import { TextAreaComponent } from '../../../atoms/text-area/text-area.component';
import { ModalHeaderComponent } from '../../../molecules/modal/modal-header/modal-header.component';
import { activityTypeOptions } from '../../activities/activities.constants';
import { Activity, ActivityTypeEnum } from '../../activities/activities.types';
import { ToastTypeEnum } from '../../toast/toast.type';

@Component({
  selector: 'app-modal-edit-activity',
  imports: [
    ModalHeaderComponent,
    MatDialogActions,
    MatDialogContent,
    InputComponent,
    AlertComponent,
    ButtonComponent,
    DropdownComponent,
    MatCheckboxModule,
    FormsModule,
    MatInputModule,
    MatFormFieldModule,
    ReactiveFormsModule,
    TextAreaComponent,
  ],
  templateUrl: './modal-create-edit-activity.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ModalCreateEditActivityComponent implements OnInit {
  static overlayDataType: { activity?: Activity | null };

  protected RequestStateEnum = RequestStateEnum;
  protected AlertTypeEnum = AlertTypeEnum;

  private readonly activityService = inject(ActivityService);
  private readonly modalService = inject(ModalService);
  private readonly toastService = inject(ToastService);
  private readonly activityStore = inject(ActivityStore);

  private readonly activity = signal<Activity | null>(null);
  private readonly updateForm = effect(() => {
    const activity = this.activity();
    if (!activity) return;

    this.form.setValue({
      date: activity.scheduledDate ?? null,
      description: activity.description ?? null,
      hasStartTime: !!activity.scheduledTime,
      time: activity.scheduledTime ?? null,
      title: activity.title ?? null,
      type: activity.type ?? null,
    });
  });

  readonly activityTypeOptions = activityTypeOptions;
  readonly alertMsg = signal<string>('');
  readonly requestState = this.activityStore.requestState;
  readonly form = new FormGroup({
    date: new FormControl<string | null>(null, Validators.required),
    description: new FormControl<string | null>(null),
    hasStartTime: new FormControl<boolean>(false),
    time: new FormControl<string | null>({ value: null, disabled: true }),
    title: new FormControl<string | null>(null, Validators.required),
    type: new FormControl<ActivityTypeEnum | null>(null, Validators.required),
  });

  constructor(
    @Inject(MAT_DIALOG_DATA) public data: typeof ModalCreateEditActivityComponent.overlayDataType
  ) {}

  ngOnInit(): void {
    if (this.data?.activity) {
      this.activityService.getActivityById(this.data.activity.id).subscribe({
        next: (response) => this.activity.set(response),
        error: () => this.toastService.addToast('Fehler beim Laden', ToastTypeEnum.ERROR),
      });
    }

    this.form.controls.hasStartTime.valueChanges.subscribe((hasStartTime) => {
      if (hasStartTime) {
        this.form.controls.time.enable();
        this.form.controls.time.addValidators(Validators.required);
      } else {
        this.form.controls.time.disable();
        this.form.controls.time.setValue(null);
        this.form.controls.time.removeValidators(Validators.required);
      }
    });
  }

  createActivity(): void {
    const fc = this.form.controls;

    this.activityStore
      .create(
        fc.type.value!,
        fc.title.value!,
        fc.description?.value ?? '',
        dayjs(fc.date.value),
        fc.hasStartTime.value ? fc.time.value! : null
      )
      .then(() => this.modalService.close());
  }

  updateActivity(): void {
    const fc = this.form.controls;

    this.activityStore
      .update(
        this.activity()!.id,
        fc.type.value!,
        fc.title.value!,
        fc.description?.value ?? '',
        dayjs(fc.date.value),
        fc.hasStartTime.value ? fc.time.value! : null
      )
      .then(() => this.modalService.close());
  }

  deleteActivity(): void {
    this.activityStore.delete(this.activity()!.id).then(() => this.modalService.close());
  }
}
